import React from 'react';
import PropTypes from 'prop-types';

function norm(s) {
  return String(s || '').trim().toLowerCase();
}

function filterTree(nodes, q) {
  const query = norm(q);
  if (!query) return nodes;
  const walk = (n) => {
    const nameMatch = norm(n.name).includes(query);
    const kids = Array.isArray(n.children) ? n.children : [];
    const filteredKids = kids.map(walk).filter(Boolean);
    if (nameMatch || filteredKids.length) return { ...n, children: filteredKids };
    return null;
  };
  return (Array.isArray(nodes) ? nodes : []).map(walk).filter(Boolean);
}

function collectAllIds(nodes, set) {
  const arr = Array.isArray(nodes) ? nodes : [];
  for (const n of arr) {
    set.add(String(n.id));
    if (Array.isArray(n.children) && n.children.length) collectAllIds(n.children, set);
  }
}

function collectRootIds(nodes) {
  return (Array.isArray(nodes) ? nodes : []).map((n) => String(n.id));
}

function getSelectedPathIds(nodes, selectedIds) {
  const target = new Set([
    selectedIds?.main,
    selectedIds?.sub,
    selectedIds?.subSub
  ].filter(Boolean).map(String));
  if (!target.size) return new Set();

  const out = new Set();
  const walk = (n, pathIds) => {
    const nextPath = [...pathIds, String(n.id)];
    if (target.has(String(n.id))) {
      nextPath.forEach((id) => out.add(id));
      return true;
    }
    const kids = Array.isArray(n.children) ? n.children : [];
    for (const c of kids) {
      if (walk(c, nextPath)) return true;
    }
    return false;
  };

  for (const root of Array.isArray(nodes) ? nodes : []) {
    if (walk(root, [])) break;
  }
  return out;
}

function CategoryTreeNav({
  tree,
  selected,
  onSelect,
  onClear,
  className,
  showSearch,
  showHeader,
  variant,
  maxHeight
}) {
  const isMobile = variant === 'mobile';
  const [q, setQ] = React.useState('');
  const [expanded, setExpanded] = React.useState(() => new Set(collectRootIds(tree)));

  // When tree changes (loaded), expand roots by default.
  React.useEffect(() => {
    setExpanded(new Set(collectRootIds(tree)));
  }, [tree]);

  const selectedPathIds = React.useMemo(() => getSelectedPathIds(tree, selected), [tree, selected]);
  const filteredTree = React.useMemo(() => filterTree(tree, q), [tree, q]);
  const searchExpanded = React.useMemo(() => {
    const s = new Set();
    if (norm(q)) collectAllIds(filteredTree, s);
    return s;
  }, [filteredTree, q]);

  const isExpanded = (id) => {
    const k = String(id);
    if (norm(q)) return searchExpanded.has(k);
    if (selectedPathIds.has(k)) return true;
    return expanded.has(k);
  };

  const toggleExpanded = (id) => {
    const k = String(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const renderNode = (node, level = 0) => {
    const isSelected =
      selected?.main === node.id ||
      selected?.sub === node.id ||
      selected?.subSub === node.id;

    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const open = hasChildren ? isExpanded(node.id) : false;

    const indentPx = isMobile ? level * 12 + 6 : level * 5 + 5;

    return (
      <div key={node.id} className={isMobile ? 'space-y-1' : 'space-y-1.5'}>
        <div
          className={
            isMobile
              ? `w-full rounded-xl transition-colors ${isSelected
                ? 'bg-primary-50'
                : 'bg-white hover:bg-secondary-50'
              } shadow-[0_1px_0_0_rgba(15,23,42,0.06)]`
              : `w-full rounded-md transition-colors border ${isSelected
                ? 'bg-primary-50 border-primary-200'
                : 'bg-white border-secondary-200 hover:bg-secondary-50'
              }`
          }
          style={{ paddingLeft: `${indentPx}px` }}
        >
          <div className="flex items-stretch">
            {hasChildren ? (
              <button
                type="button"
                onClick={() => toggleExpanded(node.id)}
                className={
                  isMobile
                    ? 'px-2 text-secondary-500 hover:text-secondary-800'
                    : 'px-1 text-secondary-500 hover:text-secondary-800'
                }
                aria-label={open ? 'Свернуть' : 'Развернуть'}
                title={open ? 'Свернуть' : 'Развернуть'}
              >
                <span className="inline-block w-4 text-center select-none">
                  {open ? '▾' : '▸'}
                </span>
              </button>
            ) : (
              <div className="w-3" />
            )}
            <button
              type="button"
              onClick={() => onSelect(node)}
              className={
                isMobile
                  ? `flex-1 text-left rounded-xl py-2.5 pr-2 ${isSelected ? 'text-primary-900' : 'text-secondary-800'
                  }`
                  : `flex-1 text-left rounded-md py-1.5 ${isSelected ? 'text-primary-900' : 'text-secondary-800'
                  }`
              }
            >
              <div className="flex items-center justify-between gap-2">
                <span className={isMobile ? 'text-sm leading-5 font-medium' : 'text-[13px] leading-4 font-medium pr-2'}>
                  {node.name}
                </span>
                <span className={
                  isMobile
                    ? 'text-xs text-secondary-600 bg-secondary-50 border border-secondary-100 rounded-full px-2 py-0.5'
                    : 'text-xs text-secondary-500 px-2'
                }>
                  {typeof node.profileCount === 'number' ? node.profileCount : '—'}
                </span>
              </div>
            </button>
          </div>
        </div>

        {hasChildren && open && (
          <div className={isMobile ? 'space-y-1 ml-3 pl-1' : 'space-y-1 ml-2 border-l border-secondary-100 pl-2'}>
            {node.children.map((child) => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`${isMobile
        ? `flex flex-col ${className || ''}`
        : `bg-white border border-secondary-200 rounded-2xl shadow-soft flex flex-col ${className || ''}`
        }`.trim()}
    >
      {/* Header */}
      {showHeader && (
        <div className="px-4 pt-4 pb-3 border-b border-secondary-100">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-secondary-900 tracking-tight">Категории</h2>
            <button
              type="button"
              onClick={onClear}
              className="text-xs px-2 py-1 rounded-md border border-secondary-200 bg-white hover:bg-secondary-50 text-secondary-700"
            >
              Сбросить
            </button>
          </div>

          {showSearch && (
            <div className="mt-3">
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Поиск…"
                className="w-full rounded-lg border border-secondary-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
          )}
        </div>
      )}

      {/* Body (scrollable) */}
      <div
        className={`${isMobile ? 'px-0 py-1' : 'px-4 py-3'} overflow-y-auto min-h-0`.trim()}
        style={maxHeight ? { maxHeight, WebkitOverflowScrolling: 'touch' } : { WebkitOverflowScrolling: 'touch' }}
      >
        <div className={isMobile ? 'space-y-2' : 'space-y-2'}>
          {Array.isArray(filteredTree) && filteredTree.length > 0 ? (
            filteredTree.map((node) => renderNode(node))
          ) : (
            <p className="text-sm text-secondary-500">Категории не найдены.</p>
          )}
        </div>
      </div>
    </aside>
  );
}

CategoryTreeNav.propTypes = {
  tree: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      level: PropTypes.number,
      parentId: PropTypes.string,
      profileCount: PropTypes.number,
      children: PropTypes.array
    })
  ),
  selected: PropTypes.shape({
    main: PropTypes.string,
    sub: PropTypes.string,
    subSub: PropTypes.string
  }),
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  className: PropTypes.string,
  showSearch: PropTypes.bool,
  showHeader: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'mobile']),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};

CategoryTreeNav.defaultProps = {
  tree: [],
  selected: null,
  className: '',
  showSearch: true,
  showHeader: true,
  variant: 'default',
  maxHeight: null
};

export default CategoryTreeNav;
